import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import EditableCell from "../../../components/BI/EditableCell";
import useAuthFetch from "../../../hooks/useAuthFetch";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * ConfiguracionSection - Configuración de prioridad de transferencias.
 *
 * Muestra una tabla editable agrupada por bodega destino con:
 * - Bodega Destino, Bodega Origen, Prioridad, Cobertura Mínima (días), Cobertura Máxima (días)
 * - Botones up/down para reordenar prioridad
 * - Celdas editables para cobertura_minima_dias y cobertura_maxima_dias
 * - Persistencia vía PUT /bi/configuracion-transferencia/{bodegaDestinoId}
 */
export function ConfiguracionSection() {
  const token = useSelector((state) => state.app.user?.token);
  const authFetch = useAuthFetch();

  const [configuraciones, setConfiguraciones] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);

  // ============================================================
  // Fetch bodegas activas + configuración on mount
  // ============================================================
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch active bodegas
      const bodegasRes = await authFetch(
        `bodegas?filter={"where":{"activo":1},"order":["nombre asc"]}`
      );
      const bodegasData = await bodegasRes.json();
      setBodegas(bodegasData);

      // Fetch existing configuraciones
      const configRes = await fetch(`${API_URL}/bi/configuracion-transferencia`, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!configRes.ok) {
        const errText = await configRes.text().catch(() => "Error desconocido");
        throw new Error(errText || `HTTP ${configRes.status}`);
      }
      const configData = await configRes.json();
      setConfiguraciones(configData);
    } catch (err) {
      setError(err.message || "Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  }, [token, authFetch]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  // ============================================================
  // Group: show ALL active bodegas as destino,
  // for each destino show all OTHER active bodegas as posible origen
  // ============================================================
  const groupedByDestino = React.useMemo(() => {
    if (bodegas.length === 0) return [];

    // Index existing configs by destino+origen
    const configMap = {};
    configuraciones.forEach((config) => {
      const destId = config.idBodegaDestino || config.id_bodega_destino;
      const origId = config.idBodegaOrigen || config.id_bodega_origen;
      const key = `${destId}_${origId}`;
      configMap[key] = config;
    });

    return bodegas.map((bodegaDestino) => {
      const origenBodegas = bodegas.filter((b) => b.id !== bodegaDestino.id);

      const items = origenBodegas.map((bodegaOrigen, idx) => {
        const existingConfig = configMap[`${bodegaDestino.id}_${bodegaOrigen.id}`];
        if (existingConfig) {
          return {
            id: existingConfig.id,
            idBodegaOrigen: bodegaOrigen.id,
            bodegaOrigenNombre: bodegaOrigen.nombre || `Bodega ${bodegaOrigen.id}`,
            prioridad: existingConfig.prioridad,
            coberturaMinimaDias: existingConfig.coberturaMinimaDias ?? existingConfig.cobertura_minima_dias ?? 7,
            coberturaMaximaDias: existingConfig.coberturaMaximaDias ?? existingConfig.cobertura_maxima_dias ?? 15,
          };
        }
        // No config yet — assign default priority based on position
        return {
          id: null,
          idBodegaOrigen: bodegaOrigen.id,
          bodegaOrigenNombre: bodegaOrigen.nombre || `Bodega ${bodegaOrigen.id}`,
          prioridad: idx + 1,
          coberturaMinimaDias: 7,
          coberturaMaximaDias: 15,
        };
      });

      // Sort by prioridad
      items.sort((a, b) => a.prioridad - b.prioridad);
      // Re-number priorities sequentially
      items.forEach((item, idx) => {
        item.prioridad = idx + 1;
      });

      return {
        bodegaDestinoId: bodegaDestino.id,
        bodegaDestinoNombre: bodegaDestino.nombre || `Bodega ${bodegaDestino.id}`,
        items,
      };
    });
  }, [bodegas, configuraciones]);

  // ============================================================
  // Save configuration for a specific bodega destino
  // ============================================================
  const saveConfigForDestino = useCallback(
    async (bodegaDestinoId, items) => {
      setSaving(true);
      setSaveSuccess(null);
      try {
        const body = items.map((item) => ({
          idBodegaOrigen: item.idBodegaOrigen,
          prioridad: item.prioridad,
          coberturaMinimaDias: item.coberturaMinimaDias,
          coberturaMaximaDias: item.coberturaMaximaDias,
        }));

        const response = await fetch(
          `${API_URL}/bi/configuracion-transferencia/${bodegaDestinoId}`,
          {
            method: "PUT",
            headers: {
              "Content-type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          const errText = await response.text().catch(() => "Error al guardar");
          throw new Error(errText || `HTTP ${response.status}`);
        }
        setSaveSuccess(bodegaDestinoId);
        setTimeout(() => setSaveSuccess(null), 3000);
        // Refresh data
        await fetchData();
      } catch (err) {
        setError(err.message || "Error al guardar configuración");
      } finally {
        setSaving(false);
      }
    },
    [token, fetchData]
  );

  // ============================================================
  // Move priority up/down
  // ============================================================
  const handleMovePriority = useCallback(
    (bodegaDestinoId, itemIndex, direction) => {
      setConfiguraciones((prev) => {
        const updated = [...prev];
        const group = groupedByDestino.find(
          (g) => g.bodegaDestinoId === bodegaDestinoId
        );
        if (!group) return prev;

        const items = [...group.items];
        const targetIndex = direction === "up" ? itemIndex - 1 : itemIndex + 1;
        if (targetIndex < 0 || targetIndex >= items.length) return prev;

        // Swap priorities
        const tempPrioridad = items[itemIndex].prioridad;
        items[itemIndex].prioridad = items[targetIndex].prioridad;
        items[targetIndex].prioridad = tempPrioridad;

        // Persist immediately
        const reordered = [...items].sort((a, b) => a.prioridad - b.prioridad);
        saveConfigForDestino(bodegaDestinoId, reordered);

        return prev;
      });
    },
    [groupedByDestino, saveConfigForDestino]
  );

  // ============================================================
  // Handle inline edit for cobertura values
  // ============================================================
  const handleSaveCobertura = useCallback(
    async (bodegaDestinoId, itemIndex, field, newValue) => {
      const group = groupedByDestino.find(
        (g) => g.bodegaDestinoId === bodegaDestinoId
      );
      if (!group) return;

      const updatedItems = group.items.map((item, idx) => {
        if (idx === itemIndex) {
          return { ...item, [field]: newValue };
        }
        return item;
      });

      await saveConfigForDestino(bodegaDestinoId, updatedItems);
    },
    [groupedByDestino, saveConfigForDestino]
  );

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Cargando configuración...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">Error: {error}</span>
          <button
            onClick={fetchData}
            className="ml-auto text-sm text-red-700 underline hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (groupedByDestino.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No hay configuración de transferencias definida.</p>
        <p className="text-sm mt-2">
          Las prioridades se configuran en la tabla ConfiguracionTransferencia.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Configure el orden de prioridad de las bodegas origen para transferencias.
            Prioridad 1 significa que es la primera bodega de la que se transfiere stock.
          </p>
        </div>
        {saving && (
          <span className="text-sm text-blue-600 flex items-center gap-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
            Guardando...
          </span>
        )}
      </div>

      {groupedByDestino.map((group) => (
        <div
          key={group.bodegaDestinoId}
          className="border border-gray-200 rounded-lg overflow-hidden"
        >
          {/* Group header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">
              Bodega Destino: {group.bodegaDestinoNombre}
            </h3>
            {saveSuccess === group.bodegaDestinoId && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardado
              </span>
            )}
          </div>

          {/* Table */}
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-left">
                <th className="px-4 py-2 font-medium">Bodega Origen</th>
                <th className="px-4 py-2 font-medium text-center">Prioridad</th>
                <th className="px-4 py-2 font-medium text-center">Cobertura Mínima (días)</th>
                <th className="px-4 py-2 font-medium text-center">Cobertura Máxima (días)</th>
                <th className="px-4 py-2 font-medium text-center">Reordenar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {group.items.map((item, idx) => (
                <tr key={item.id || `${item.idBodegaOrigen}-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800 font-medium">
                    {item.bodegaOrigenNombre}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-semibold text-sm">
                      {item.prioridad}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <EditableCell
                      value={item.coberturaMinimaDias}
                      min={1}
                      max={365}
                      validate={(val) => {
                        if (val >= item.coberturaMaximaDias) {
                          return "Debe ser menor que la cobertura máxima";
                        }
                        return null;
                      }}
                      onSave={(newValue) =>
                        handleSaveCobertura(
                          group.bodegaDestinoId,
                          idx,
                          "coberturaMinimaDias",
                          newValue
                        )
                      }
                      disabled={saving}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <EditableCell
                      value={item.coberturaMaximaDias}
                      min={1}
                      max={365}
                      validate={(val) => {
                        if (val <= item.coberturaMinimaDias) {
                          return "Debe ser mayor que la cobertura mínima";
                        }
                        return null;
                      }}
                      onSave={(newValue) =>
                        handleSaveCobertura(
                          group.bodegaDestinoId,
                          idx,
                          "coberturaMaximaDias",
                          newValue
                        )
                      }
                      disabled={saving}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() =>
                          handleMovePriority(group.bodegaDestinoId, idx, "up")
                        }
                        disabled={idx === 0 || saving}
                        className={`p-1 rounded transition-colors ${
                          idx === 0 || saving
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-500 hover:text-purple-700 hover:bg-purple-50"
                        }`}
                        title="Subir prioridad"
                        aria-label={`Subir prioridad de ${item.bodegaOrigenNombre}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() =>
                          handleMovePriority(group.bodegaDestinoId, idx, "down")
                        }
                        disabled={idx === group.items.length - 1 || saving}
                        className={`p-1 rounded transition-colors ${
                          idx === group.items.length - 1 || saving
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-500 hover:text-purple-700 hover:bg-purple-50"
                        }`}
                        title="Bajar prioridad"
                        aria-label={`Bajar prioridad de ${item.bodegaOrigenNombre}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default ConfiguracionSection;
